import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Chip,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import api from '../../../services/api';
import { intoleranceOptions, dietaryPreferenceOptions } from '../../../utils/entityOptions';

/**
 * Formulaire d'edition d'un Client (Coach)
 */
const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // V2.0: Dialog de recalcul des macros
  const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);
  const [affectedPlansCount, setAffectedPlansCount] = useState(0);
  const [recalculateLoading, setRecalculateLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    weight: '',
    height: '',
    gender: 'male',
    goals: '',
    dietaryPreferences: [],
    intolerances: [],
    medicalConditions: '',
    sportProfile: {
      nap: 1.5,
      period: 'base',
    },
  });

  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchClient();
  }, [id]);

  const fetchClient = async () => {
    try {
      setFetchLoading(true);
      const data = await api.get(`/coach/clients/${id}`);

      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        age: data.age || '',
        weight: data.weight || '',
        height: data.height || '',
        gender: data.gender || 'male',
        goals: data.goals || '',
        dietaryPreferences: data.dietaryPreferences || [],
        intolerances: data.intolerances || [],
        medicalConditions: data.medicalConditions?.join(', ') || '',
        sportProfile: {
          nap: data.sportProfile?.nap || 1.5,
          period: data.sportProfile?.period || 'base',
        },
      });
    } catch (err) {
      console.error('Erreur lors du chargement du client:', err);
      setError('Impossible de charger les informations du client.');
    } finally {
      setFetchLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName) {
      errors.firstName = 'Prenom requis';
    }

    if (!formData.lastName) {
      errors.lastName = 'Nom requis';
    }

    if (!formData.email) {
      errors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    if (formData.age && (formData.age < 1 || formData.age > 120)) {
      errors.age = 'Age invalide';
    }

    if (formData.weight && (formData.weight < 1 || formData.weight > 500)) {
      errors.weight = 'Poids invalide';
    }

    if (formData.height && (formData.height < 50 || formData.height > 300)) {
      errors.height = 'Taille invalide';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle nested sportProfile fields
    if (name.startsWith('sportProfile.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        sportProfile: {
          ...prev.sportProfile,
          [field]: value,
        },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      // Prepare data
      const submitData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        medicalConditions: formData.medicalConditions
          ? formData.medicalConditions.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };

      const response = await api.put(`/coach/clients/${id}`, submitData);

      // V2.0: Verifier si les macros sont obsoletes
      if (response._meta?.nutritionFieldsChanged) {
        setAffectedPlansCount(response._meta.affectedPlansCount || 0);
        setShowRecalculateDialog(true);
        setLoading(false);
        return;
      }

      setSuccess('Client mis a jour avec succes !');

      // Redirect after short delay
      setTimeout(() => {
        navigate('/coach/clients');
      }, 1500);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  // V2.0: Recalculer les macros
  const handleRecalculate = async () => {
    try {
      setRecalculateLoading(true);
      await api.post(`/coach/clients/${id}/recalculate-macros`, {
        applyToWeekPlans: false
      });

      setShowRecalculateDialog(false);
      setSuccess('Client mis a jour et macros recalculees !');

      setTimeout(() => {
        navigate('/coach/clients');
      }, 1500);
    } catch (err) {
      console.error('Erreur recalcul:', err);
      setError(err.response?.data?.message || 'Erreur lors du recalcul des macros.');
      setShowRecalculateDialog(false);
    } finally {
      setRecalculateLoading(false);
    }
  };

  // V2.0: Passer sans recalculer
  const handleSkipRecalculate = () => {
    setShowRecalculateDialog(false);
    setSuccess('Client mis a jour avec succes !');

    setTimeout(() => {
      navigate('/coach/clients');
    }, 1500);
  };

  if (fetchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Modifier client - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <IconButton onClick={() => navigate('/coach/clients')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h1">
            Modifier le client
          </Typography>
        </Stack>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                {/* Section: Informations personnelles */}
                <Typography variant="h3">
                  Informations personnelles
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Prenom"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={!!formErrors.firstName}
                    helperText={formErrors.firstName}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Nom"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={!!formErrors.lastName}
                    helperText={formErrors.lastName}
                    required
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Telephone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                  />
                </Stack>

                <FormControl fullWidth>
                  <InputLabel>Genre</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    label="Genre"
                  >
                    <MenuItem value="male">Masculin</MenuItem>
                    <MenuItem value="female">Feminin</MenuItem>
                    <MenuItem value="other">Autre</MenuItem>
                  </Select>
                </FormControl>

                <Divider />

                {/* Section: Donnees physiques */}
                <Typography variant="h3">
                  Donnees physiques
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Age"
                    name="age"
                    type="number"
                    value={formData.age}
                    onChange={handleChange}
                    error={!!formErrors.age}
                    helperText={formErrors.age}
                    inputProps={{ min: 1, max: 120 }}
                  />
                  <TextField
                    fullWidth
                    label="Poids (kg)"
                    name="weight"
                    type="number"
                    value={formData.weight}
                    onChange={handleChange}
                    error={!!formErrors.weight}
                    helperText={formErrors.weight}
                    inputProps={{ min: 1, step: 0.1 }}
                  />
                  <TextField
                    fullWidth
                    label="Taille (cm)"
                    name="height"
                    type="number"
                    value={formData.height}
                    onChange={handleChange}
                    error={!!formErrors.height}
                    helperText={formErrors.height}
                    inputProps={{ min: 50, max: 300 }}
                  />
                </Stack>

                <Divider />

                {/* Section: Profil sportif */}
                <Typography variant="h3">
                  Profil sportif
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Niveau d'activite (NAP)</InputLabel>
                    <Select
                      name="sportProfile.nap"
                      value={formData.sportProfile.nap}
                      onChange={handleChange}
                      label="Niveau d'activite (NAP)"
                    >
                      <MenuItem value={1.3}>Sedentaire / 1-2 seances par semaine</MenuItem>
                      <MenuItem value={1.5}>Quotidien dynamique / 3-5 seances par semaine</MenuItem>
                      <MenuItem value={1.7}>Travail physique / 5+ seances par semaine</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Periode d'entrainement</InputLabel>
                    <Select
                      name="sportProfile.period"
                      value={formData.sportProfile.period}
                      onChange={handleChange}
                      label="Periode d'entrainement"
                    >
                      <MenuItem value="base">Preparation / Base</MenuItem>
                      <MenuItem value="intensification">Intensification</MenuItem>
                      <MenuItem value="competition">Competition</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Divider />

                {/* Section: Objectifs et sante */}
                <Typography variant="h3">
                  Objectifs et sante
                </Typography>

                <TextField
                  fullWidth
                  label="Objectifs"
                  name="goals"
                  value={formData.goals}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  placeholder="Perte de poids, prise de masse, performance..."
                />

                <TextField
                  fullWidth
                  label="Conditions medicales"
                  name="medicalConditions"
                  value={formData.medicalConditions}
                  onChange={handleChange}
                  placeholder="Separez par des virgules"
                  helperText="Ex: Diabete, Hypertension"
                />

                <Divider />

                {/* Section: Regimes et intolerances */}
                <Typography variant="h3">
                  Regimes et intolerances
                </Typography>

                <FormControl fullWidth>
                  <InputLabel>Intolerances alimentaires</InputLabel>
                  <Select
                    name="intolerances"
                    multiple
                    value={formData.intolerances}
                    onChange={handleChange}
                    input={<OutlinedInput label="Intolerances alimentaires" />}
                    renderValue={(selected) => (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={intoleranceOptions.find(o => o.value === value)?.label || value}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  >
                    {intoleranceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Preferences alimentaires</InputLabel>
                  <Select
                    name="dietaryPreferences"
                    multiple
                    value={formData.dietaryPreferences}
                    onChange={handleChange}
                    input={<OutlinedInput label="Preferences alimentaires" />}
                    renderValue={(selected) => (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={dietaryPreferenceOptions.find(o => o.value === value)?.label || value}
                            size="small"
                          />
                        ))}
                      </Stack>
                    )}
                  >
                    {dietaryPreferenceOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider />

                {/* Actions */}
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/coach/clients')}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                    Enregistrer
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>

        {/* V2.0: Dialog de recalcul des macros */}
        <Dialog
          open={showRecalculateDialog}
          onClose={() => !recalculateLoading && setShowRecalculateDialog(false)}
        >
          <DialogTitle>Donnees physiques modifiees</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Les donnees physiques de ce client ont ete modifiees (poids, taille, age ou profil sportif).
            </Typography>
            {affectedPlansCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {affectedPlansCount} plan(s) actif(s) utilisent les anciennes valeurs de calcul.
              </Alert>
            )}
            <Typography>
              Voulez-vous recalculer les besoins nutritionnels maintenant ?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Les futurs plans utiliseront automatiquement les nouvelles valeurs.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleSkipRecalculate}
              disabled={recalculateLoading}
            >
              Plus tard
            </Button>
            <Button
              variant="contained"
              onClick={handleRecalculate}
              disabled={recalculateLoading}
            >
              {recalculateLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              Recalculer maintenant
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default EditClient;
